import { useEffect, useState } from "react";
import axios from "axios";
import { Eye, TrendingUp } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const VISIT_KEY = "tiger_visit_day";

function todayStr() {
  return new Date().toISOString().slice(0, 10);
}

export default function VisitorCounter() {
  const [count, setCount] = useState(null);
  const [weekly, setWeekly] = useState(null);

  useEffect(() => {
    let mounted = true;
    (async () => {
      try {
        const lastVisit = localStorage.getItem(VISIT_KEY);
        const today = todayStr();
        const method = lastVisit !== today ? "post" : "get";
        const [visitRes, weeklyRes] = await Promise.all([
          axios[method](`${API}/visits`),
          axios.get(`${API}/visits/weekly`),
        ]);
        if (!mounted) return;
        setCount(visitRes.data?.count ?? 0);
        setWeekly(weeklyRes.data || null);
        if (lastVisit !== today) {
          localStorage.setItem(VISIT_KEY, today);
        }
      } catch (e) {
        console.error("Visitor counter failed", e);
      }
    })();
    return () => {
      mounted = false;
    };
  }, []);

  if (count === null) return null;

  const digits = String(count).padStart(4, "0").split("");
  const days = weekly?.days ?? [];
  const maxDay = Math.max(1, ...days.map((d) => d.count));

  return (
    <div
      className="inline-flex flex-col items-center gap-3"
      data-testid="visitor-counter"
    >
      <span className="inline-flex items-center gap-2 rounded-full border-[3px] border-tiger-sand bg-tiger-orange px-3 py-1 font-heading text-xs font-bold uppercase tracking-wider text-white shadow-[4px_4px_0_#FFFDF7]">
        <Eye className="h-4 w-4" /> Tiger Watchers
      </span>
      <div className="flex items-center gap-1.5" data-testid="visitor-count">
        {digits.map((d, i) => (
          <span
            key={i}
            className="grid h-12 w-9 place-items-center rounded-xl border-[3px] border-tiger-sand bg-tiger-deep font-heading text-2xl font-bold text-sun shadow-[4px_4px_0_#FFFDF7]"
          >
            {d}
          </span>
        ))}
      </div>
      <p className="font-body text-xs text-tiger-sand/70">
        Friends who visited so far
      </p>

      {weekly && (
        <div
          className="mt-1 w-full max-w-[220px] rounded-2xl border-[3px] border-tiger-sand/50 bg-white/10 px-3 py-2 shadow-[4px_4px_0_#FFFDF7]"
          data-testid="visitor-weekly"
        >
          <div className="flex items-center justify-between font-body text-[10px] font-bold uppercase tracking-widest text-tiger-sand/80">
            <span className="inline-flex items-center gap-1">
              <TrendingUp className="h-3 w-3" /> Last 7 days
            </span>
            <span data-testid="visitor-weekly-total">{weekly.total}</span>
          </div>
          <div className="mt-2 flex h-10 items-end justify-between gap-1">
            {days.map((d) => {
              const h = Math.max(4, (d.count / maxDay) * 100);
              const dayLabel = new Date(d.date + "T00:00:00Z").toLocaleDateString(
                undefined,
                { weekday: "narrow" },
              );
              return (
                <div
                  key={d.date}
                  className="flex flex-1 flex-col items-center gap-1"
                  title={`${d.date}: ${d.count} visit${d.count === 1 ? "" : "s"}`}
                >
                  <div
                    className="w-full rounded-sm bg-sun"
                    style={{ height: `${h}%` }}
                  />
                  <span className="font-body text-[9px] font-bold text-tiger-sand/70">
                    {dayLabel}
                  </span>
                </div>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
}
